import React from 'react';
import { Authenticator, ThemeProvider, CheckboxField, Link as AmplifyLink } from '@aws-amplify/ui-react';
import { useNavigate, useLocation } from 'react-router-dom';
import '@aws-amplify/ui-react/styles.css';
import { 
  Box, 
  Image, 
  Flex, 
  Text,
  Grid,
  GridItem,
  useColorModeValue,
  keyframes,
  usePrefersReducedMotion,
  Icon,
  Link
} from '@chakra-ui/react';
import { 
  AiOutlineRobot, 
  AiOutlineLock, 
  AiOutlineRadarChart 
} from 'react-icons/ai';

// Enhanced animations
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const subtleFloat = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-5px); }
  100% { transform: translateY(0px); }
`;

// Subtle gradient animation
const gradientShift = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const AmplifyAuth = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || "/admin/create-widget";
  const prefersReducedMotion = usePrefersReducedMotion();
  
  // Enhanced color scheme with green and dark accent
  const primaryColor = '#44b474'; // Enqelt green
  const darkColor = '#2e2e2e'; // Enqelt dark
  const pureWhite = '#FFFFFF';
  const offWhite = '#F5F5F7';
  const lightGreen = '#E8F5E9';
  const subtleGray = '#86868b';
  const darkText = darkColor;
  const glowColor = `${primaryColor}40`;
  
  // Animation settings remain the same
  const animation = prefersReducedMotion
    ? undefined
    : `${fadeIn} 0.6s cubic-bezier(0.16, 1, 0.3, 1)`;
  
  const gradientAnimation = prefersReducedMotion
    ? undefined
    : `${gradientShift} 15s ease infinite`;
  
  // Theme updates with new color scheme
  const theme = {
    name: 'enqelt-modern-theme',
    tokens: {
      colors: {
        font: {
          interactive: { value: primaryColor },
          primary: { value: darkText },
        },
        brand: {
          primary: {
            10: { value: '#e6f7ee' },
            20: { value: '#d0f0de' },
            40: { value: '#a0e1be' }, 
            60: { value: '#71d29d' }, 
            80: { value: '#55c987' }, 
            90: { value: primaryColor }, 
            100: { value: '#3a9b62' },
          },
        },
        background: {
          primary: { value: pureWhite },
        },
        border: {
          primary: { value: '#e6e6e6' },
        },
      },
      components: {
        authenticator: {
          router: {
            borderRadius: { value: '24px' },
            boxShadow: { value: '0 12px 40px rgba(0, 0, 0, 0.12)' },
            backgroundColor: { value: pureWhite },
          },
          form: {
            padding: { value: '2rem' },
          },
        },
        button: {
          borderRadius: { value: '10px' },
          fontSize: { value: '0.95rem' },
          fontWeight: { value: 600 },
          transition: { value: 'all 0.2s cubic-bezier(0.16, 1, 0.3, 1)' },
          primary: {
            backgroundColor: { value: primaryColor },
            _hover: {
              backgroundColor: { value: '#3a9b62' },
              transform: { value: 'translateY(-1px)' },
              boxShadow: { value: '0 4px 12px rgba(68, 180, 116, 0.3)' }
            },
            _active: {
              transform: { value: 'translateY(0)' },
              boxShadow: { value: 'none' }
            }
          }
        },
        field: {
          borderRadius: { value: '10px' },
          transition: { value: 'all 0.2s ease' },
          backgroundColor: { value: offWhite },
          borderColor: { value: '#e2e2e2' },
          color: { value: darkText },
          _focus: {
            borderColor: { value: primaryColor },
            boxShadow: { value: `0 0 0 2px ${primaryColor}30` }
          },
          label: {
            fontSize: { value: '0.9rem' },
            fontWeight: { value: '500' },
            letterSpacing: { value: '0.01em' },
            color: { value: darkText }
          },
          input: {
            fontSize: { value: '1rem' },
            _hover: {
              borderColor: { value: '#c9c9c9' }
            }
          }
        }
      }
    }
  };

  // Custom components
  const components = {
    Header() {
      return null; // Remove default header
    },
    Footer() {
      return null; // Remove default footer
    },
    SignUp: {
      FormFields() {
        return (
          <>
            <Authenticator.SignUp.FormFields />
            <Flex direction="column" marginTop="1rem">
              <CheckboxField
                name="agreed_to_terms"
                label={
                  <Text fontSize="0.85rem" color={darkText}>
                    I accept the{' '}
                    <AmplifyLink
                      href="https://enqelt.com/integritetspolicy-enqelt-ab/"
                      isExternal={true}
                      color={primaryColor}
                    >
                      Terms of Service
                    </AmplifyLink>
                  </Text>
                }
                value="yes"
                required={true}
              />
            </Flex>
          </>
        );
      }
    }
  };

  // Simple form field labels
  const formFields = {
    signIn: {
      username: {
        placeholder: 'Enter your email',
        label: 'Email',
        isRequired: true
      },
      password: {
        placeholder: 'Enter your password',
        label: 'Password',
        isRequired: true
      }
    },
    signUp: {
      email: {
        placeholder: 'Enter your email',
        label: 'Email',
        isRequired: true
      },
      password: {
        placeholder: 'Create a password',
        label: 'Password',
        isRequired: true
      },
      confirm_password: {
        placeholder: 'Confirm your password',
        label: 'Confirm Password',
        isRequired: true
      }
    }
  };

  // Add validation service for the Terms of Service checkbox
  const services = {
    async validateCustomSignUp(formData) {
      if (!formData.agreed_to_terms) {
        return {
          agreed_to_terms: 'You must accept the Terms of Service'
        };
      }
      return {};
    }
  };

  return (
    <Box 
      position="relative"
      overflow="hidden"
      bg={darkColor}
      minH="100vh"
    >
      {/* Modern geometric background pattern */}
      <Box
        position="absolute"
        top="0"
        left="0"
        right="0"
        bottom="0"
        bgSize="cover"
        opacity="0.03"
        backgroundImage={`url('data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.15"%3E%3Cpath d="M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')`}
        zIndex="0"
      />
      
      {/* Green gradient accent elements */}
      <Box
        position="absolute"
        top="10%"
        right="15%"
        width="30%"
        height="40%"
        borderRadius="full"
        bgGradient={`radial-gradient(circle, ${primaryColor}20 0%, ${primaryColor}05 60%, transparent 80%)`}
        filter="blur(60px)"
        zIndex="0"
      />
      
      <Box
        position="absolute"
        bottom="10%"
        left="10%"
        width="35%"
        height="30%"
        borderRadius="full"
        bgGradient={`radial-gradient(circle, ${primaryColor}25 0%, ${primaryColor}08 60%, transparent 80%)`}
        filter="blur(70px)"
        zIndex="0"
      />

      {/* Modern diagonal line accent */}
      <Box
        position="absolute"
        top="0"
        right="0"
        width="100%"
        height="100%"
        opacity="0.1"
        background={`linear-gradient(135deg, transparent 49.5%, ${primaryColor} 49.5%, ${primaryColor} 50.5%, transparent 50.5%)`}
        zIndex="0"
      />

      <Grid 
        templateColumns={{ base: "1fr", md: "1fr 1fr" }}
        minH="100vh"
        zIndex="1"
        position="relative"
      >
        {/* Left Section - Branding */}
        <GridItem
          padding={{ base: "3rem 2rem", md: "4rem" }}
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          textAlign="center"
          position="relative"
          overflow="hidden"
        >
          {/* Brand accent elements */}
          <Box
            position="absolute"
            top="20%"
            left="0"
            width="100%"
            height="1px"
            bg={`linear-gradient(90deg, transparent, ${primaryColor}40, transparent)`}
            zIndex="0"
          />
          
          {/* Brand content */}
          <Flex 
            direction="column" 
            alignItems="center" 
            zIndex="1"
            animation={animation}
            maxW="450px"
          >
            {/* Logo */}
            <Box 
              position="relative" 
              mb={10}
              width="100%"
            >
              {/* Enqelt branding */}
              <Flex 
                direction="column" 
                alignItems="center"
              >
                <Text 
                  fontSize={{ base: "5xl", md: "6xl" }}
                  fontWeight="800"
                  letterSpacing="-0.02em"
                  color={primaryColor}
                  mb={2}
                  textTransform="uppercase"
                  fontFamily="'SF Pro Display', 'Inter', sans-serif"
                >
                  ENQELT
                </Text>
              </Flex>
            </Box>
            
            {/* Divider element - thicker */}
            <Box
              width="60px"
              height="4px"
              bg={primaryColor}
              borderRadius="full"
              mb={8}
            />
            
            {/* Headline - improved contrast */}
            <Text 
              as="h1" 
              fontSize={{ base: "xl", md: "2xl" }}
              fontWeight="600"
              mb={6}
              color="white"
              letterSpacing="tight"
            >
              AI Widget Platform
            </Text>
            
            {/* Description - improved contrast */}
            <Text
              fontSize={{ base: "md" }}
              color="rgba(255,255,255,0.8)"
              maxW="400px"
              mx="auto"
              lineHeight="tall"
              mb={10}
            >
              Build and deploy powerful AI-powered widgets with enterprise-grade security and advanced analytics.
            </Text>
            
            {/* Feature blocks - modern style with better contrast */}
            <Grid 
              templateColumns="repeat(3, 1fr)" 
              gap={4} 
              width="100%"
              mb={10}
            >
              {[
                {icon: AiOutlineRobot, label: "AI-Powered"},
                {icon: AiOutlineLock, label: "Enterprise Security"},
                {icon: AiOutlineRadarChart, label: "Analytics Engine"}
              ].map((item, i) => (
                <Box
                  key={i}
                  p={4}
                  borderRadius="xl"
                  bg="rgba(255,255,255,0.06)"
                  backdropFilter="blur(10px)"
                  borderWidth="1px"
                  borderColor="rgba(255,255,255,0.1)"
                  display="flex"
                  flexDir="column"
                  alignItems="center"
                  _hover={{
                    transform: "translateY(-2px)",
                    bg: "rgba(255,255,255,0.1)",
                    boxShadow: `0 8px 16px rgba(0,0,0,0.2), 0 0 0 1px ${primaryColor}40`
                  }}
                  transition="all 0.3s ease"
                >
                  <Box
                    borderRadius="full"
                    bg={`${primaryColor}30`}
                    p={2}
                    mb={3}
                  >
                    <Icon as={item.icon} w={5} h={5} color={primaryColor} />
                  </Box>
                  <Text fontSize="sm" color="white" fontWeight="medium">{item.label}</Text>
                </Box>
              ))}
            </Grid>
            
            {/* Testimonial - glass morphism style */}
            <Box
              mt={6}
              p={5}
              borderRadius="xl"
              bg="rgba(255,255,255,0.05)"
              backdropFilter="blur(10px)"
              borderWidth="1px"
              borderColor="rgba(255,255,255,0.1)"
              width="100%"
            >
              <Text
                fontSize="sm"
                fontStyle="italic"
                color="rgba(255,255,255,0.8)"
                textAlign="center"
              >
                "The most intuitive AI widget platform for modern digital experiences."
              </Text>
            </Box>
          </Flex>
        </GridItem>

        {/* Right Section - Authentication */}
        <GridItem
          padding={{ base: "2rem 1.5rem", md: "4rem" }}
          display="flex"
          flexDirection="column"
          justifyContent="center"
          position="relative"
        >
          {/* Apple-style auth container with improved contrast */}
          <Box
            maxW="450px" 
            w="100%" 
            mx="auto"
            borderRadius="24px"
            bg="rgba(255,255,255,0.95)"
            backdropFilter="blur(30px)"
            overflow="hidden"
            boxShadow="0 20px 50px rgba(0,0,0,0.3), 0 0 0 1px rgba(255,255,255,0.1)"
            position="relative"
            zIndex="1"
            animation={animation}
          >
            {/* Subtle green top accent */}
            <Box 
              position="absolute" 
              top="0" 
              left="0" 
              right="0" 
              height="5px" 
              bgGradient={`linear-gradient(90deg, ${primaryColor}, ${primaryColor}80)`}
            />
            
            {/* Header */}
            <Box p={8} pb={5} textAlign="center">
              <Text 
                fontSize="xl"
                fontWeight="600" 
                letterSpacing="tight"
                color={darkText}
                mb={1}
              >
                Welcome back
              </Text>
              <Text 
                fontSize="sm"
                color={subtleGray}
              >
                Sign in to your account to continue
              </Text>
            </Box>

            {/* Authenticator Component */}
            <Box>
              <ThemeProvider theme={theme}>
                <Authenticator
                  loginMechanisms={['email']}
                  components={components}
                  formFields={formFields}
                  services={services}
                  hideSignUp={false}
                >
                  {({ signOut }) => {
                    // Once authenticated, redirect to the intended page
                    setTimeout(() => {
                      navigate(from, { replace: true });
                    }, 0);
                    
                    return null;
                  }}
                </Authenticator>
              </ThemeProvider>
            </Box>

            {/* Footer with subtle branding */}
            <Box p={6} textAlign="center" borderTopWidth="1px" borderColor="#f0f0f0">
              <Text fontSize="xs" color={subtleGray} mb={1}>
                © {new Date().getFullYear()} Enqelt. All rights reserved.
              </Text>
              <Flex justifyContent="center" alignItems="center">
                <Box as="span" w="3px" h="3px" borderRadius="full" bg={primaryColor} mx={1} />
                <Box as="span" w="3px" h="3px" borderRadius="full" bg={primaryColor} opacity="0.6" mx={1} />
                <Box as="span" w="3px" h="3px" borderRadius="full" bg={primaryColor} opacity="0.3" mx={1} />
              </Flex>
            </Box>
          </Box>
        </GridItem>
      </Grid>
    </Box>
  );
};

export default AmplifyAuth;